import React, {useEffect, useState} from 'react';
import {Cell, Legend, Pie, PieChart, ResponsiveContainer} from 'recharts';
import {useNavigate} from 'react-router-dom';
import Table from '../../Table/Table';
import {useLazyMetricsInspectionsMissedQuery} from '../../../store/builder/inspections.api';
import Select from 'react-select';
import Loader from '../../Loader';
import CustomLoader from '../../CustomLoader/CustomLoader';
import {useTranslation} from '../../../hooks/useTranslation';

const cellKeys = [
  'inspection_date',
  'inspection_id',
  'equipment_name',
  'facility_name',
  'period',
];

const CorporateDashboardCard = ({
  graph,
  nfcPercentage = 0,
  missedPercentage = 0,
  actionsPercentage = 0,
  chartData,
  item,
  agencyId,
  data,
}) => {
  const {t} = useTranslation();
  const chartFirstColor =
    +item?.data?.equipment?.percentage === 0 ? '#2aa58d' : '#DA6767';
  const COLOR_BY_NAME = {
    'Out of service (Current)': chartFirstColor,
    'In service': '#E9EAEB',
    'Manually Decommissioned (Current)': '#AEB3B7',
  };
  const getColorForEntry = entry => COLOR_BY_NAME[entry?.name] ?? '#E9EAEB';
  const navigate = useNavigate();
  const [chosenMissedFilters, setChosenMissedFilters] = useState({
    period: null,
    filter_for_period: 'weekly',
  });
  const [filterPeriodOptions, setFilterPeriodOptions] = useState('weekly');
  const [filterLastPeriodShortOptions, setFilterLastPeriodShortOptions] =
    useState('');

  const headers = [
    t('corporateDashboardCard.table.headers.inspectionDate'),
    t('corporateDashboardCard.table.headers.inspections'),
    t('corporateDashboardCard.table.headers.equipmentId'),
    t('corporateDashboardCard.table.headers.facility'),
    t('corporateDashboardCard.table.headers.period'),
  ];

  const periodFilters = [
    {value: '', label: t('corporateDashboardCard.table.filters.all')},
    {value: 'daily', label: t('corporateDashboardCard.table.filters.daily')},
    {value: 'weekly', label: t('corporateDashboardCard.table.filters.weekly')},
    {
      value: 'monthly',
      label: t('corporateDashboardCard.table.filters.monthly'),
    },
    {
      value: 'quarterly',
      label: t('corporateDashboardCard.table.filters.quarterly'),
    },
    {
      value: 'semiannual',
      label: t('corporateDashboardCard.table.filters.semiannual'),
    },
    {value: 'annual', label: t('corporateDashboardCard.table.filters.annual')},
  ];

  const periodFilters2 = [
    {
      value: 'weekly',
      label: t('corporateDashboardCard.table.filters.lastWeek'),
    },
    {
      value: 'monthly',
      label: t('corporateDashboardCard.table.filters.lastMonth'),
    },
    {
      value: 'quarterly',
      label: t('corporateDashboardCard.table.filters.lastQuarter'),
    },
    {
      value: 'semiannual',
      label: t('corporateDashboardCard.table.filters.last6Months'),
    },
    {value: 'annual', label: t('corporateDashboardCard.table.filters.ttm')},
  ];

  const [
    getMetricsMissed,
    {data: metricsMissedData, isFetching: metricsMissedIsLoading},
  ] = useLazyMetricsInspectionsMissedQuery();

  const transformToObjectArray = originalObject => {
    const result = [];
    for (const key in originalObject) {
      if (key !== 'percentage') {
        const {key: name, value} = originalObject[key];
        result.push({name, value});
      }
    }
    return result;
  };
  const transformedArray = transformToObjectArray(item?.data?.equipment);

  useEffect(() => {
    if (agencyId) {
      getMetricsMissed({
        agency: agencyId,
        filter_for_period: filterPeriodOptions,
        period: filterLastPeriodShortOptions,
        limit: 10,
      });
    }
  }, [filterPeriodOptions, filterLastPeriodShortOptions, data]);

  return (
    <div className='corporate__card'>
      <div className='corporate__card-head'>
        <div className='corporate__card-detail'>
          <img
            className='corporate__card-avatar'
            src={item?.agency?.logo}
            alt='avatar'
          />
          <div className='corporate__card-text'>
            <p className='corporate__card-title'>{item?.agency?.title}</p>
            <p className='corporate__card-subtitle'>
              {item?.agency?.region_label}
            </p>
          </div>
        </div>
        <div
          className='corporate__card-detail'
          onClick={() => navigate(`/dashboard?agency=${item?.agency?.id}`)}
        >
          <span>{t('corporateDashboardCard.details')}</span>
          <img src='/assets/icons/agency-arrow.svg' alt='icon' />
        </div>
      </div>
      <div className='corporate__card-block'>
        <div className='corporate__card-stats'>
          <div className='corporate__card-text'>
            <p className='corporate__card-statsSubTitle'>
              {t('corporateDashboardCard.stats.oos')}
            </p>
            <p className='corporate__card-statsTitle'>
              {item?.data?.equipment['Out of service (Current)']?.value}
              <span>
                /
                {item?.data?.equipment['In service']?.value +
                  item?.data?.equipment['Out of service (Current)']?.value}
              </span>
            </p>
          </div>
        </div>

        <div className='corporate__card-stats'>
          <div className='corporate__card-left graph'>
            <div className='corporate__card-text'>
              <p className='corporate__card-statsSubTitle'>
                {t('corporateDashboardCard.stats.outOfServiceManual')}
              </p>
              <div className='corporate__card-stats-content'>
                <p className='corporate__card-statsTitle'>
                  {item?.data?.equipment['Manually Decommissioned (Current)']
                    ?.value || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          className='corporate__card-stats'
          style={{borderRight: '1px solid #e9eaeb'}}
        >
          <div className='corporate__card-left graph'>
            <div className='corporate__card-text'>
              <p className='corporate__card-statsSubTitle'>
                Open Return to Service Inspections (Current)
              </p>
              <div className='corporate__card-stats-content'>
                <p className='corporate__card-statsTitle'>
                  {item?.data?.open_stats?.open_rts}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className='corporate__card-stats'>
          <div className='corporate__card-left graph'>
            <div className='corporate__card-text'>
              <p className='corporate__card-statsSubTitle'>
                Open Corrective Actions (Current)
              </p>
              <div className='corporate__card-stats-content'>
                <p className='corporate__card-statsTitle'>
                  {item?.data?.open_stats?.open_ca}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          className='corporate__card-stats'
          style={{borderLeft: '1px solid #e9eaeb'}}
        >
          <div className='corporate__card-middle graph'>
            <div className='corporate__card-text'>
              <p className='corporate__card-statsSubTitle'>
                {t('corporateDashboardCard.stats.complianceRating')}
              </p>
              <div className='corporate__card-stats-content'>
                <p className='corporate__card-statsTitle'>
                  {item?.agency_compliance}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='corporate__card-fullWidth'>
        <div className='corporate__card-table-filters'>
          <div className='chart__header'>
            <p className='chart__header-title'>
              {t('corporateDashboardCard.table.missedInspections')}
            </p>
            <div className='chart__filters'>
              <Select
                defaultValue={{
                  value: 'weekly',
                  label: t('corporateDashboardCard.table.filters.lastWeek'),
                }}
                options={periodFilters2}
                onChange={selected => {
                  setFilterPeriodOptions(selected.value);
                }}
                menuPosition={'absolute'}
              />
              <Select
                defaultValue={{
                  value: '',
                  label: t('corporateDashboardCard.table.filters.period'),
                }}
                options={periodFilters}
                onChange={selected => {
                  setFilterLastPeriodShortOptions(selected.value);
                }}
                menuPosition={'absolute'}
              />
            </div>
          </div>
        </div>
        <div className='corporate__card-table'>
          {metricsMissedIsLoading ? (
            <CustomLoader />
          ) : (
            <Table
              headers={headers}
              cellKeys={cellKeys}
              customRowType='fbo_custom_row'
              rows={metricsMissedData?.inspections}
              isSmallPaddings
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CorporateDashboardCard;
